import { convertAmountToCurrency } from "@/utils/trade";
import { formatAmount } from "@/utils/format";
import { ISymbolInfo } from "@/services/trade/symbols";


type SwapKey = "swapBuy" | "swapSell";
type SwapType = "PIPS" | "PERCENTS" | "NOT_DEFINED" | "UNKNOWN";

export function formatSwapDisplay(
    amount: number,
    convertedAmount: number | null,
    fromCurrency: string,
    toCurrency: string
): string {
    const base = formatAmount(amount, fromCurrency);

    if (!convertedAmount || isNaN(convertedAmount) || fromCurrency === toCurrency) {
        return base;
    }

    const converted = formatAmount(convertedAmount, toCurrency);
    return `${base} (${converted})`;
}

export function computeSwap(
    symbolInfo: ISymbolInfo,
    lotSize: number,
    swapKey: SwapKey,
    isMTR: boolean,
    price: number
): number {
    const contractSize = symbolInfo?.contractSize ?? 0;
    const swap = symbolInfo?.ex?.[swapKey] ?? 0;
    const digits = symbolInfo?.digits ?? 2;
    const swapType: SwapType = symbolInfo?.ex?.swapType;

    let result = Number(lotSize) * swap;

    if (!isMTR) {
        return Number(result.toFixed(2));
    }

    if (swapType === "PIPS") {
        result = (Number(lotSize) * contractSize * swap) / Math.pow(10, digits);
    } else {
        result = (Number(lotSize) * contractSize * price * (swap / 100)) / 360;
    }

    return Number(result.toFixed(2));
}

interface CalculateSwapsParams {
    lotSize: string | number;
    symbolInfo: ISymbolInfo;
    isMTR: boolean;
    bid: number;
    ask: number;
    swapType: SwapType;
    accountCurrency: string;
    symbols: string[];
    mainCurrency: string;
}

interface CalculateSwapsResult {
    swapBuy: number;
    swapBuyDisplayValue: string;
    swapSell: number;
    swapSellDisplayValue: string;
}

export function calculateSwaps({
    lotSize,
    symbolInfo,
    isMTR,
    bid,
    ask,
    swapType,
    accountCurrency,
    symbols,
    mainCurrency,
}: CalculateSwapsParams): CalculateSwapsResult {
    const swapBuy = computeSwap(symbolInfo, Number(lotSize), "swapBuy", isMTR, bid);
    const swapSell = computeSwap(symbolInfo, Number(lotSize), "swapSell", isMTR, ask);

    let swapCurrency = mainCurrency;

    if (swapType === "PIPS" && isMTR) {
        swapCurrency = symbolInfo?.ex?.quoteCurrency;
    }

    if (!isMTR) swapCurrency = symbolInfo?.currency;

    const currencyToCompare = isMTR
        ? accountCurrency === symbolInfo?.ex?.quoteCurrency
        : accountCurrency === symbolInfo?.currency;

    const swapBuyConverted = currencyToCompare
        ? null
        : convertAmountToCurrency({
            amount: swapBuy,
            fromCurrency: swapCurrency,
            toCurrency: accountCurrency,
            symbols,
        });

    const swapSellConverted = currencyToCompare
        ? null
        : convertAmountToCurrency({
            amount: swapSell,
            fromCurrency: swapCurrency,
            toCurrency: accountCurrency,
            symbols,
            conversionPriceType: "Bid",
        });

    return {
        swapBuy,
        swapBuyDisplayValue: formatSwapDisplay(swapBuy, swapBuyConverted, swapCurrency, accountCurrency),
        swapSell,
        swapSellDisplayValue: formatSwapDisplay(swapSell, swapSellConverted, swapCurrency, accountCurrency),
    };
}